import React, { useState, useEffect, useRef, useCallback, Fragment, useMemo } from "react";
import {
  Button,
  Divider,
  makeStyles,
  Tooltip,
  Textarea,
  Input,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  tokens,
  mergeClasses,
  Link,
  Body1,
  Caption1,
  List,
  ListItem,
} from "@fluentui/react-components";
import Stopwatch from "./Stopwatch";
import {
  CallEnd20Filled,
  MicOff24Filled,
  Mic24Filled,
  EditFilled,
  CheckmarkFilled,
  PlayFilled,
  PauseFilled,
  CallTransferRegular,
  CallTransferFilled, //init
  CallForwardRegular,
  CallForwardFilled, //bt
  PeopleCallRegular,
  PeopleCallFilled, //ct
  CallDismissRegular, //cancel transfer
  Pin16Filled,
} from "@fluentui/react-icons";
import { DtmfDialpad } from "../DtmfDialpad/DtmfDialpad";
import { CallResult } from "../CallResult/CallResult";
import { CallOptionsMenu } from "./MoreSetting";

import { screenPop } from "../../utils/acs.salesforce";

import { getIconInfoById } from "../../utils/sfIconMapper";
import SFIcon from "../SFIcon";
import PhoneSearchInput from "../PhoneSearchInput";

const amberColor = tokens.colorPaletteMarigoldForeground3;
const connectedColor = tokens.colorPaletteGreenForeground1;

const doneBtnHoverStyles = {
  backgroundColor: tokens.colorPaletteGreenBackground3,
  color: "white",
};

const rejectBtnHoverStyles = {
  backgroundColor: tokens.colorPaletteRedForeground3,
  color: "white",
};
const fixedBottomPanelRow2ColBtnHoverStyles = {
  color: tokens.colorBrandBackgroundHover,
  backgroundColor: "white",
};

const isNumeric = (obj) => {
  return (
    (typeof obj === "number" || typeof obj === "string") &&
    obj !== "" &&
    !isNaN(obj - parseFloat(obj))
  );
};

const useStyles = makeStyles({
  //list : START
  listRoot: {
    width: "auto",
    padding: 0,
    //for btngrp to be at bottom
    position: "relative",
    height: "408px", //"350px",//w.r.t adapter xml 350
    display: "flex",
    flexDirection: "column",
  },
  ulRoot: { flex: "1", minHeight: "0", overflowY: "auto" },
  transferSubGroup: {
    marginTop: "6px",
    overflow: "visible",
    flex: "unset",
    minHeight: "unset",
  },
  transferSubList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "0",
    margin: "0",
    listStyle: "none",
  },
  paddingRightMd: {
    paddingRight: "0.85rem",
  },
  enclosedTransferLegLi: {
    borderTop: "2px solid var(--colorNeutralBackground2Pressed)",
    borderBottom: "2px solid var(--colorNeutralBackground2Pressed)",
    borderRight: "2px solid var(--colorNeutralBackground2Pressed)",
    borderTopRightRadius: "5px",
    borderBottomRightRadius: "5px",
  },
  enclosedTransferLegLiBorderLeft: {
    borderLeft: "2px solid var(--colorNeutralBackground2Pressed)",
  },
  listItem: {
    padding: "0.2rem 0 0.2rem 0.85rem",
    borderBottom: "1px solid var(--neutralStroke2, #e1e1e1)",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground4,
    },
    borderTopLeftRadius: "5px",
    borderBottomLeftRadius: "5px",
  },
  listItemWrapupConnectedState: {
    borderLeft: `8px solid ${tokens.colorNeutralForeground4}`,
  },
  listItemWithBorderConnectedState: {
    borderLeft: `8px solid ${connectedColor}`,
  },
  listItemWithBorderConnectingState: {
    borderLeft: `8px solid ${amberColor}`,
  },
  listItemNoBorder: {
    borderLeft: "none",
  },
  listItemDeactivated: {
    pointerEvents: "none",
    backgroundColor: tokens.colorNeutralBackground2Hover,
  },
  listItemGrid: {
    display: "grid",
    gridTemplateColumns: "70% 30%",
    gap: 12,
    alignItems: "center",
  },
  //col1 -> future if icon is added
  // -------- Column 2 --------
  textDecorWithUnderline: {
    textDecoration: "underline",
    textDecorationColor: "currentColor",
  },
  boldText: {
    fontWeight: "bold",
  },
  required: {
    color: tokens.colorStatusDangerForeground1,
  },
  col2: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "hidden",
  },

  col2Row1: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  nameLink: {
    color: "inherit",
    textDecoration: "none",
    minWidth: 0,
    display: "inline-block",
  },

  col2Row2: {
    fontSize: "0.85rem",
  },

  // -------- Column 3 --------
  col3: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: 4,
  },

  col3Row1: {
    fontSize: "18px",
    lineHeight: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--neutralForeground3, #605E5C)",
  },
  col3Row2: {
    color: "var(--neutralForeground3, #605E5C)",
    fontWeight: 200,
    cursor: "pointer",
    ":hover": {
      textDecoration: "underline",
      color: tokens.colorNeutralForeground1,
    },
  },
  editLink: {
    color: "var(--neutralForeground3, #605E5C)",
    textDecoration: "none",
    fontWeight: 500,
    cursor: "pointer",
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
  },
  queueLine: {
    color: "var(--neutralForegroundDisabled, #8a8886)",
  },
  //list : end
  wrapper: {
    /*minHeight: "90vh",
    background:
      "radial-gradient(circle at top, #f3f4ff 0, #f9fafb 45%, #ffffff 100%)",
    padding: "16px",*/
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    width: "100%",
    maxWidth: "620px",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
    padding: "16px 20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  titleMain: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  mainCallerDiv: {
    display: "flex",
    width: "100%",
  },
  callerDiv: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    flexDirection: "column",
    marginLeft: "17px",
  },
  callerName: {
    color: "#111827",
    fontWeight: 600,
    marginTop: "2px",
  },
  callerNumber: {
    color: "#4b5563",
    fontSize: "12px",
    marginTop: "-2px",
  },
  statusRow: {
    gap: "8px",
    flexWrap: "wrap",
    display: "flex !important",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "14px",
  },
  statusPill: {
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 600,
    border: "1px solid #e5e7eb",
  },
  avatar: {
    width: "40px",
    height: "40px",
    borderRadius: "999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "radial-gradient(circle at 30% 0%, #fff 0, #e0e7ff 40%, #818cf8 100%)",
    color: "#111827",
    fontWeight: 700,
    fontSize: "16px",
    position: "relative",
    overflow: "hidden",
    marginLeft: "4px",
  },
  avatarPulse: {
    position: "relative",
  },
  controlsRow: {
    columnGap: "8px",
    display: "flex !important",
    marginRight: "8px",
  },
  circleBtn: {
    minWidth: "0",
    width: "48px",
    height: "48px",
    borderRadius: "999px",
  },
  circleBtnLg: {
    width: "56px",
    height: "56px",
  },
  footerText: {
    marginTop: "4px",
    textAlign: "center",
    fontSize: "12px",
    color: "#605e5c",
  },
  inputWrapper: {
    //for bottom placement
    width: "98%",
    margin: "0% 1% 2% 1%",
    //for bottom placement
  },
  rejectButton: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    color: "white",
    ":hover": rejectBtnHoverStyles,
    ":active": rejectBtnHoverStyles,
    ":hover:active": rejectBtnHoverStyles,
    ":focus": rejectBtnHoverStyles,
  },
  rejectButtonDeactivated: {
    backgroundColor: tokens.colorNeutralStroke1,
  },
  nameDeactivated: {
    color: "#605e5c",
  },
  relRecHeader: {
    marginLeft: "4%",
    fontWeight: 600,
  },
  cancelTransferBtnDiv: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    margin: "12px 0",
  },
  transferInfoLabel: {
    //borderColor:"#605e5c",
    color: "#605e5c",
  },
  mt4: {
    marginTop: "2px", //"4px",
  },
  fixedBottomPanel: {
    columnGap: "8px",
    //position: "relative",
    display: "flex !important",
    flexDirection: "column" /* stack row1 and row2 vertically */,
    justifyContent: "space-between",
    padding: "2% 0%",
    margin: "0%",
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground4,
  },
  fixedBottomPanelRowAssignment: {
    display: "grid",
    gridTemplateColumns: "15% 70% 15%",
    alignItems: "center",
    width: "100%",
  },
  assignmentDivider: {
    margin: "5px 0",
  },
  fixedBottomPanelRowAssignmentIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: tokens.colorNeutralForeground4,
  },
  fixedBottomPanelRowAssignmentName: {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal",
  },
  fixedBottomPanelRow1: {
    flex: "1",
  },
  fixedBottomPanelRow2: {
    flex: "1",
    display: "flex",
    justifyContent: "space-between",
  },
  fixedBottomPanelRow2Col: {
    flex: "1" /* equal width columns */,
    display: "flex",
    flexDirection: "column" /* stack button + label vertically */,
    alignItems: "center" /* center horizontally */,
  },
  fixedBottomPanelRow2ColBtn: {
    backgroundColor: "white",
    borderRadius: "2rem",
    color: tokens.colorNeutralForeground4,
    ":hover": fixedBottomPanelRow2ColBtnHoverStyles,
    //':active': fixedBottomPanelRow2ColBtnHoverStyles,
    //':hover:active': fixedBottomPanelRow2ColBtnHoverStyles,
    //':focus': fixedBottomPanelRow2ColBtnHoverStyles,
  },
  fixedBottomPanelRow2ColBtnDisabled: {
    color: tokens.colorNeutralForegroundDisabled,
  },
  fixedBottomPanelRow2ColBtnLabel: {
    marginTop: "4px",
  },
  subText: {
    fontSize: "0.75rem",
  },
  doneButton: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: "white",
    ":hover": doneBtnHoverStyles,
    ":active": doneBtnHoverStyles,
    ":hover:active": doneBtnHoverStyles,
    ":focus": doneBtnHoverStyles,
  },
  doneButtonDisabled: {
    backgroundColor: tokens.colorNeutralBackground2Hover,
    color: tokens.colorNeutralBackground3Pressed,
  },
});

export default function ActiveCallScreen({
  callMap,
  upsertCallMap,
  setActiveConnectedCallId,
  activeConnectedCallId,
  setWrapupConnectedCallId,
  wrapupConnectedCallId,
  onWrapupDone,
  onWrapupEdit,
  call,
  transferCallMap,
  removeFromTransferCallMap,
  assignMetaRef,
  deletedAssignedRecIdRef,
  setDeletedAssignedRecId,
  recToConfirmBeforeAssignment,
  setRecToConfirmBeforeAssignment,
  onHangUp,
  onMute,
  onUnmute,
  onResume,
  onHold,
  onBlindTransfer,
  onConsultedTransfer,
  onCancelTransfer,
  callTransferState,
  handleDtmf,
  notes,
  onChangeNotes,
  showEditSubject,
  subject,
  onChangeSubject,
  callResult,
  onChangeCallResult,
  callResultGroup,
  onChangeCallResultGroup,
  isSwitchingCall,
}) {
  const [showWrapup, setShowWrapup] = useState(false);
  const classes = useStyles({ showWrapup, showEditSubject });

  const NOTES_TEXTAREA_HEIGHT_KEY = "unaric_notesTextareaHeight";
  const [notesTextareaElement, setNotesTextareaElement] = useState(null);
  const [notesTextareaHeight, setNotesTextareaHeight] = useState(
    () => localStorage.getItem(NOTES_TEXTAREA_HEIGHT_KEY) || undefined,
  );
  const notesResizeObserverRef = useRef(null);

  const notesTextareaCallbackRef = useCallback((el) => {
    setNotesTextareaElement(el);
  }, []);

  useEffect(() => {
    if (notesResizeObserverRef.current) {
      notesResizeObserverRef.current.disconnect();
      notesResizeObserverRef.current = null;
    }

    if (!notesTextareaElement) {
      return undefined;
    }

    if (notesTextareaHeight) {
      notesTextareaElement.style.height = notesTextareaHeight;
    }

    const observer = new ResizeObserver((entries) => {
      const h = notesTextareaElement.offsetHeight || entries[0]?.contentRect?.height;
      if (h) {
        const hPx = `${Math.round(h)}px`;
        localStorage.setItem(NOTES_TEXTAREA_HEIGHT_KEY, hPx);
        setNotesTextareaHeight(hPx);
      }
    });

    observer.observe(notesTextareaElement);
    notesResizeObserverRef.current = observer;

    return () => {
      observer.disconnect();
      if (notesResizeObserverRef.current === observer) {
        notesResizeObserverRef.current = null;
      }
    };
  }, [notesTextareaElement, notesTextareaHeight]);

  const wrapupConnectedCallObj = callMap?.get(wrapupConnectedCallId);
  const isWrapupConnectedCallObjInEditMode =
    (wrapupConnectedCallObj?.isWrapup && wrapupConnectedCallObj?.isWrapupInEditMode) ?? false;

  const activeConnectedCallObj = callMap?.get(activeConnectedCallId);

  const activeConnectedCallState = activeConnectedCallObj?.callState;
  const isActiveConnectedCallInActiveCallState = ["Connected", "LocalHold", "RemoteHold"].includes(
    activeConnectedCallState,
  );
  const activeConnectedCallTransferState = activeConnectedCallObj?.callTransferState;
  let activeConnectedCallTransferType = activeConnectedCallObj?.transferType;

  let activeConnectedCallWhoId = activeConnectedCallObj?.whoId || "";
  let activeConnectedCallWhatId = activeConnectedCallObj?.whatId || "";

  //so that call1 is ongoing, call2 ring, its contact is shown as ques for call2, after answering call1,
  useEffect(() => {
    //else error - Cannot update a component (App) while rendering a different component (ActiveCallScreen)
    if (
      (activeConnectedCallWhoId &&
        recToConfirmBeforeAssignment?.recordId &&
        activeConnectedCallWhoId.toLowerCase().substring(0, 15) ===
          recToConfirmBeforeAssignment.recordId.toLowerCase().substring(0, 15)) ||
      (activeConnectedCallWhatId &&
        recToConfirmBeforeAssignment?.recordId &&
        activeConnectedCallWhatId.toLowerCase().substring(0, 15) ===
          recToConfirmBeforeAssignment.recordId.toLowerCase().substring(0, 15))
    ) {
      setRecToConfirmBeforeAssignment({
        recordId: null,
        recordName: null,
        objectType: null,
      });
    }
  }, [activeConnectedCallWhoId, activeConnectedCallWhatId, recToConfirmBeforeAssignment?.recordId]);

  let transferLegofActiveConnectedCallObj = null;
  const transferLegsByParentId = useMemo(() => {
    const grouped = new Map();
    for (const entry of transferCallMap.values()) {
      const parentId = entry.parentCallId;
      if (parentId === activeConnectedCallId) {
        transferLegofActiveConnectedCallObj = entry;
      }
      if (!grouped.has(parentId)) grouped.set(parentId, []);
      grouped.get(parentId).push(entry);
    }
    return grouped;
  }, [transferCallMap]);

  let isActiveConnectedCallMuted = false;
  let childLeg = transferLegsByParentId?.get(activeConnectedCallId) ?? [];
  if (childLeg?.length > 0 && !childLeg[0]?.isHold) {
    //only when child leg is there and in resumed mode, fixed grey section will work for it
    isActiveConnectedCallMuted = childLeg[0]?.isMuted ?? false;
  } else {
    isActiveConnectedCallMuted = activeConnectedCallObj?.isMuted ?? false;
  }

  const [showAutocomplete, setShowAutocomplete] = useState(false);

  //to call from complete transfer
  const [selectedUser, setSelectedUser] = useState(null);
  const [hasText, setHasText] = useState(false);
  const [transferSearchValue, setTransferSearchValue] = useState("");
  let dirSrchInputValue = useRef();

  const canShowInitTransferBtn =
    (selectedUser?.id || hasText) &&
    (activeConnectedCallTransferType === "bt" ||
      (activeConnectedCallTransferType === "ct" && !activeConnectedCallObj.transferredCall));

  const isCompleteTransferTooltip =
    activeConnectedCallTransferType === "bt" ||
    (activeConnectedCallTransferType === "ct" &&
      activeConnectedCallTransferState &&
      !["Ringing", "Connecting"].includes(activeConnectedCallTransferState));

  const hasUserOrAutocomplete = showAutocomplete || selectedUser;
  const canShowCancelTransferBtn =
    (hasUserOrAutocomplete && activeConnectedCallTransferType === "bt") ||
    (activeConnectedCallTransferType === "ct" && !activeConnectedCallObj.transferredCall);

  const dirSrchHasUserOrText = selectedUser || hasText;

  const showTransferInfo = activeConnectedCallTransferState === "Transferring";

  const transferInfoLabel =
    activeConnectedCallTransferType === "bt" || activeConnectedCallTransferState === "Transferring"
      ? "Transferring to "
      : "Connecting call to ";

  const statusLabel = (callState) => {
    switch (callState) {
      case "Ringing":
        return "Ringing…";
      case "Connecting":
        return "Connecting…";
      case "Connected":
        return "In Call";
      case "LocalHold":
        return "On Hold";
      case "RemoteHold":
        return "Held Remotely";
      case "Disconnected":
        return "Call Ended";
      default:
        return callState || "Idle";
    }
  };

  const statusColor = (callState) => {
    if (callState === "Connected") return connectedColor;
    if (callState === "Connecting" || callState === "Ringing" || callState === "EarlyMedia")
      return amberColor;
    if (callState === "Disconnected") return "#d13438"; // red
    return "#605e5c"; // neutral
  };

  const disableBtnInFixedSection = () => {
    let isDisable = !isActiveConnectedCallInActiveCallState;
    return isDisable;
  };

  const handleDefaultAssignment = (rec) => {
    const whoIdPrefixes = ["003", "00Q"];
    if (whoIdPrefixes.includes(rec.recordId.substring(0, 3))) {
      upsertCallMap(activeConnectedCallId, {
        whoId: rec.recordId,
        whoName: rec?.recordName,
        whoType: rec?.objectType,
      });
    } else {
      upsertCallMap(activeConnectedCallId, {
        whatId: rec.recordId,
        whatName: rec?.recordName,
        whatType: rec?.objectType,
      });
    }
  };

  const handleCancelTransferClick = () => {
    setShowAutocomplete(false);
    setSelectedUser(null);
    setHasText(false);
    setTransferSearchValue("");
    if (activeConnectedCallTransferType === "ct") {
      onCancelTransfer(activeConnectedCallObj);
    }
    activeConnectedCallTransferType = null;
    removeFromTransferCallMap(activeConnectedCallObj.transferredCall);
    upsertCallMap(activeConnectedCallId, {
      transferredCall: null,
      callTransferState: null,
      transferType: null,
      forwardToPhone: null,
      forwardToUPN: null,
    });
  };

  const handleInitTransferClick = () => {
    let currValInDirSearch = dirSrchInputValue?.current || "";
    let value = {};
    let fwdTo = "";
    if (selectedUser) {
      value = selectedUser;
      fwdTo = selectedUser?._raw?.userPrincipalName || "";
    } else if (currValInDirSearch && isNumeric(currValInDirSearch)) {
      value = {
        id: currValInDirSearch,
        type: "phoneNumber",
        displayName: currValInDirSearch,
      };
    }
    const target =
      value.id +
      "#mirage#" +
      (value.type ? value.type : "microsoftTeamsUserId") +
      "#mirage#" +
      fwdTo +
      "#mirage#" +
      (value?.displayName || "");

    setSelectedUser(null);
    setHasText(false);
    setTransferSearchValue("");
    setShowAutocomplete(false);
    if (activeConnectedCallTransferType === "bt") {
      onBlindTransfer(target, activeConnectedCallObj);
    } else {
      onConsultedTransfer(target, activeConnectedCallObj);
    }
  };

  const TransferMenu = () => {
    const [open, setOpen] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);

    return (
      <Menu
        open={open}
        onOpenChange={(_, data) => setOpen(data.open)}
        positioning={{ position: "below-start", align: "start" }}
      >
        <MenuTrigger>
          <Tooltip content="Transfer" relationship="label">
            <Button
              size="medium"
              disabled={disableBtnInFixedSection()}
              className={classes.fixedBottomPanelRow2ColBtn}
              disabledClassName={classes.fixedBottomPanelRow2ColBtnDisabled}
              icon={<CallTransferFilled />}
            />
          </Tooltip>
        </MenuTrigger>

        <MenuPopover inline={false} withArrow>
          <MenuList>
            <MenuItem
              icon={hoveredItem === "blind" ? <CallForwardFilled /> : <CallForwardRegular />}
              onMouseEnter={() => setHoveredItem("blind")}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={async () => {
                setSelectedUser(null);
                setHasText(false);
                await onHold(activeConnectedCallObj);
                //await onHold(); Error during transfer: CallingCommunicationError: Failed to add participant. Call must be in connected state,

                activeConnectedCallTransferType = "bt";
                upsertCallMap(activeConnectedCallId, { transferType: "bt" });
                setShowAutocomplete(true);
                setOpen(false);
              }}
            >
              Blind Transfer
            </MenuItem>
            <MenuItem
              icon={hoveredItem === "consulted" ? <PeopleCallFilled /> : <PeopleCallRegular />}
              onMouseEnter={() => setHoveredItem("consulted")}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={async () => {
                setSelectedUser(null);
                setHasText(false);
                await onHold(activeConnectedCallObj);
                //await onHold(); Error during transfer: CallingCommunicationError: Failed to add participant. Call must be in connected state,

                localStorage.setItem("transfertype", "ct");
                activeConnectedCallTransferType = "ct";
                upsertCallMap(activeConnectedCallId, { transferType: "ct" });
                setShowAutocomplete(true);
                setOpen(false);
              }}
            >
              Consulted Transfer
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  };

  const CallListItem = ({ callObj, index }) => {
    const callState = callObj?.callState;
    //const isRinging = !!incomingCall && !call;
    const isConnecting = callState === "Connecting";
    const isConnected = callState === "Connected";
    const isCallActive = ["Connected", "LocalHold", "RemoteHold"].includes(callState);
    const callStatusColor = statusColor(callState);

    const isHold = callObj.isHold;

    // Extract caller info
    const callerInfo = callObj?.callerInfo;
    const callerNumber = callerInfo?.callerNumber || callObj?.callFromUpn || null;

    /*const callerName =
      callerInfo?.matchedRecord?.name || callerInfo?.callerName || null;
    const recordId = callerInfo?.matchedRecord?.recordId || "";*/

    const recordId = callObj?.whoId || callObj?.whatId || "";
    let callerName = callObj.whoId
      ? callObj?.whoName || null
      : callObj.whatId
        ? callObj?.whatName
        : null;

    if (!callerName && !callObj.whoId && !callObj.whatId && callerInfo.callerName) {
      callerName = callerInfo?.callerName || null;
    }
    const isDeactivatedLi =
      isHold &&
      activeConnectedCallTransferType &&
      callObj?.id !== activeConnectedCallId &&
      callObj?.parentCallId !== activeConnectedCallId &&
      !callObj.isWrapup;

    let classesToApplyOnLi = callObj.isWrapup
      ? classes.listItemWrapupConnectedState
      : isConnecting || callState === "Ringing" || callState === "EarlyMedia"
        ? classes.listItemWithBorderConnectingState
        : isHold
          ? mergeClasses(
              classes.listItemNoBorder,
              activeConnectedCallTransferType &&
                callObj?.id !== activeConnectedCallId &&
                callObj?.parentCallId !== activeConnectedCallId
                ? classes.listItemDeactivated
                : undefined,
            )
          : classes.listItemWithBorderConnectedState; //disable other call if ts not in wrapup, wrapup is allwed to clear calls from ui

    if (callObj?.parentCallId) {
      classesToApplyOnLi = mergeClasses(classesToApplyOnLi, classes.enclosedTransferLegLi);
      if (callObj?.isHold) {
        classesToApplyOnLi = mergeClasses(
          classesToApplyOnLi,
          classes.enclosedTransferLegLiBorderLeft,
        );
      }
    } else {
      classesToApplyOnLi = mergeClasses(classesToApplyOnLi, classes.paddingRightMd);
    }

    let transferLegs = transferLegsByParentId?.get(callObj?.id) ?? [];

    return (
      <Fragment key={callObj.id}>
        <ListItem className={mergeClasses(classes.listItem, classesToApplyOnLi)}>
          <div className={classes.listItemGrid}>
            {/* -------- Column 1 icon : START -------- */}
            {/*<div className={classes.avatar}>
              {callerName?.charAt(0)?.toUpperCase() || "U"}
            </div>*/}
            {/* -------- Column 1 icon : END -------- */}

            {/* -------- Column 2 : START-------- */}
            <div className={classes.col2}>
              <div className={classes.col2Row1}>
                {callerName ? (
                  recordId ? (
                    <Link
                      href="#"
                      role="button"
                      onClick={() => {
                        screenPop({
                          type: "SOBJECT",
                          params: { recordId },
                        });
                      }}
                      className={classes.nameLink}
                      aria-label={`Open details for ${callerName}`}
                      title={callerName}
                    >
                      <Body1
                        className={mergeClasses(
                          classes.nameLink,
                          classes.callerName,
                          isDeactivatedLi && classes.nameDeactivated,
                        )}
                      >
                        {callerName}
                      </Body1>
                    </Link>
                  ) : (
                    <Body1
                      className={mergeClasses(
                        classes.nameLink,
                        classes.callerName,
                        isDeactivatedLi && classes.nameDeactivated,
                      )}
                    >
                      {callerName}
                    </Body1>
                  )
                ) : (
                  <Body1
                    className={mergeClasses(
                      classes.nameLink,
                      classes.callerName,
                      isDeactivatedLi && classes.nameDeactivated,
                    )}
                  >
                    {callerNumber}
                  </Body1>
                )}
              </div>
              {/*replaced Body1 by caption1 to decrease font */}
              <div
                className={classes.col2Row2}
                style={{ fontSize: "0.85rem", display: "inline-block" }}
              >
                <Caption1 className={classes.col2Row1}>
                  <span
                    style={{
                      color: callStatusColor,
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {statusLabel(callState)}
                  </span>
                </Caption1>
                <Caption1>
                  {isCallActive && (
                    <>
                      {" "}
                      {isHold ? (
                        <Stopwatch answerAt={callObj?.holdedAt ?? Date.now()} />
                      ) : (
                        <Stopwatch answerAt={callObj?.answeredAt ?? Date.now()} />
                      )}
                    </>
                  )}
                </Caption1>
                <Caption1> | {callObj?.direction}</Caption1>
              </div>
            </div>
            {/* -------- Column 2 : END-------- */}

            {/* -------- Column 3 : START-------- */}
            <div className={classes.col3}>
              <div className={classes.col3Row1}>
                <div className={classes.controlsRow}>
                  {!callObj.isWrapup && (
                    <>
                      {isCallActive && (
                        <Tooltip content={isHold ? "Resume" : "Hold"} relationship="label">
                          <Button
                            aria-pressed={isHold}
                            onClick={() => (isHold ? onResume(callObj) : onHold(callObj))}
                            appearance={isHold ? "outline" : "primary"}
                            size="medium"
                            disabled={
                              activeConnectedCallTransferType &&
                              callObj?.id !== activeConnectedCallId &&
                              callObj?.parentCallId !== activeConnectedCallId
                            }
                            icon={isHold ? <PlayFilled /> : <PauseFilled />}
                          />
                        </Tooltip>
                      )}

                      <Tooltip
                        content={callObj?.parentCallId ? "Cancel Transfer" : "Hang Up"}
                        relationship="label"
                      >
                        <Button
                          size="medium"
                          onClick={() => onHangUp(callObj.id)}
                          disabled={isConnecting && callObj.direction === "Inbound"}
                          className={mergeClasses(
                            classes.rejectButton,
                            isDeactivatedLi && classes.rejectButtonDeactivated,
                          )}
                          icon={<CallEnd20Filled />}
                        />
                      </Tooltip>
                    </>
                  )}

                  {callObj.isWrapup && (
                    <>
                      <Tooltip content="Edit" relationship="label">
                        <Button
                          onClick={() => onWrapupEdit(callObj)}
                          appearance="primary"
                          size="medium"
                          disabled={
                            (isActiveConnectedCallInActiveCallState &&
                              (showAutocomplete || dirSrchHasUserOrText)) ||
                            callObj?.isWrapupSaving
                          }
                          icon={<EditFilled />}
                        />
                      </Tooltip>
                      <Tooltip content="Done" relationship="label">
                        <Button
                          size="medium"
                          appearance="outline"
                          onClick={() => onWrapupDone(callObj)}
                          className={classes.doneButton}
                          disabled={
                            (isActiveConnectedCallInActiveCallState &&
                              (showAutocomplete || dirSrchHasUserOrText)) ||
                            callObj?.isWrapupSaving
                          }
                          disabledClassName={classes.doneButtonDisabled}
                          icon={<CheckmarkFilled />}
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* -------- Column 3 : END-------- */}
          </div>

          {!callObj.isWrapup && transferLegs.length > 0 && (
            <div className={classes.transferSubGroup}>
              <List className={mergeClasses(classes.ulRoot, classes.transferSubList)}>
                {transferLegs.map((transferCallObj, index) => {
                  return (
                    <CallListItem
                      key={transferCallObj.id}
                      callObj={transferCallObj}
                      index={index}
                    />
                  );
                })}
              </List>
            </div>
          )}
        </ListItem>
        {index < callMap.length - 1 && <Divider appearance="subtle" />}
      </Fragment>
    );
  };

  return (
    <div className={classes.listRoot}>
      <List className={classes.ulRoot}>
        {[...(callMap ?? new Map()).values()].map((callObj, index) => {
          return <CallListItem key={callObj.id} callObj={callObj} index={index} />;
        })}
      </List>

      {/* The following ui only depends on active connected call*/}
      <div className={classes.fixedBottomPanel}>
        {activeConnectedCallTransferType && (
          <div className={classes.fixedBottomPanelRow1}>
            {isActiveConnectedCallInActiveCallState && showAutocomplete && (
              <div className="autocomplete-container" style={{ margin: "2%", width: "96%" }}>
                <PhoneSearchInput
                  value={transferSearchValue}
                  onChange={(val) => {
                    setTransferSearchValue(val);
                    dirSrchInputValue.current = val;
                    setHasText(val.trim().length > 0);
                  }}
                  onSelect={(row) => {
                    if (!row) {
                      setSelectedUser(null);
                      return;
                    }
                    if (row.source === "microsoft") {
                      setSelectedUser({
                        id: row.recordId,
                        displayName: row.name,
                        type: "microsoftTeamsUserId",
                        _raw: row._raw,
                      });
                    } else if (row.phone) {
                      // Salesforce result: transfer to phone number
                      setSelectedUser({
                        id: row.phone,
                        displayName: row.name,
                        type: "phoneNumber",
                      });
                    }
                  }}
                />
              </div>
            )}

            {isActiveConnectedCallInActiveCallState &&
              !showAutocomplete &&
              dirSrchHasUserOrText && (
                <div className="autocomplete-container" style={{ margin: "2%" }}>
                  {showTransferInfo && (
                    <span className={classes.transferInfoLabel}>
                      {transferInfoLabel}
                      {selectedUser ? selectedUser.displayName : dirSrchInputValue.current}
                    </span>
                  )}
                </div>
              )}

            {isActiveConnectedCallInActiveCallState &&
              (showAutocomplete || dirSrchHasUserOrText) && (
                <div className={classes.cancelTransferBtnDiv}>
                  {canShowCancelTransferBtn && (
                    <Tooltip content="Cancel Transfer" relationship="label">
                      <Button
                        size="medium"
                        appearance="secondary"
                        onClick={handleCancelTransferClick}
                        iconPosition="before"
                        disabled={["Transferred", "RemoteHold", "Disconnected"].includes(
                          activeConnectedCallTransferState,
                        )}
                        icon={<CallDismissRegular />}
                      >
                        Cancel
                      </Button>
                    </Tooltip>
                  )}

                  {canShowInitTransferBtn && (
                    <Tooltip content="Init Transfer" relationship="label">
                      <Button
                        size="medium"
                        appearance="primary"
                        disabled={[
                          "Ringing",
                          "Connecting",
                          "Transferring",
                          "Transferred",
                          "RemoteHold",
                          "Disconnected",
                        ].includes(activeConnectedCallTransferState)}
                        onClick={handleInitTransferClick}
                        iconPosition="before"
                        icon={<CallTransferRegular />}
                      >
                        Transfer
                      </Button>
                    </Tooltip>
                  )}
                </div>
              )}
          </div>
        )}
        {(!activeConnectedCallTransferType ||
          (activeConnectedCallTransferType === "ct" && activeConnectedCallObj.transferredCall)) && (
          <>
            <div className={classes.fixedBottomPanelRow1}>
              {(isActiveConnectedCallInActiveCallState || isWrapupConnectedCallObjInEditMode) && (
                <>
                  <Textarea
                    className={mergeClasses(classes.inputWrapper, classes.subText)}
                    id="notes"
                    placeholder="Add call notes..."
                    value={notes || ""}
                    rows={2}
                    resize="vertical"
                    style={notesTextareaHeight ? { height: notesTextareaHeight } : undefined}
                    disabled={!!isSwitchingCall}
                    onChange={(e, data) => onChangeNotes && onChangeNotes(data.value)}
                    textarea={{
                      ref: notesTextareaCallbackRef,
                      style: notesTextareaHeight
                        ? { height: "100%", maxHeight: "125px" }
                        : undefined,
                    }}
                  />

                  {showEditSubject && (
                    <Tooltip content="Call Subject" relationship="label">
                      <Input
                        autoComplete="off"
                        className={mergeClasses(classes.inputWrapper, classes.subText)}
                        style={{ width: "98%" }}
                        value={subject || ""}
                        onChange={(e, data) => onChangeSubject && onChangeSubject(data.value)}
                        placeholder="Add call subject..."
                        id="subject"
                        disabled={!!isSwitchingCall}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </div>
            {(isActiveConnectedCallInActiveCallState || isWrapupConnectedCallObjInEditMode) && (
              <CallResult
                onChangeShowWrapup={setShowWrapup}
                callResultGroup={callResultGroup}
                onChangeCallResultGroup={onChangeCallResultGroup}
                callResult={callResult}
                onChangeCallResult={onChangeCallResult}
                isDisabled={!!isSwitchingCall}
              />
            )}
            {recToConfirmBeforeAssignment?.recordId && (
              <>
                <Divider appearance="strong" className={classes.assignmentDivider} />
                <div className={classes.fixedBottomPanelRowAssignment}>
                  <div className={classes.fixedBottomPanelRowAssignmentIcon}>
                    <Tooltip
                      content={recToConfirmBeforeAssignment?.objectType}
                      relationship="label"
                    >
                      <SFIcon iconInfo={getIconInfoById(recToConfirmBeforeAssignment?.recordId)} />
                    </Tooltip>
                  </div>

                  <div className={classes.fixedBottomPanelRowAssignmentName}>
                    <Link
                      onClick={() => {
                        screenPop({
                          type: "SOBJECT",
                          params: {
                            recordId: recToConfirmBeforeAssignment.recordId,
                          },
                        });
                      }}
                      style={{ color: "#111827" }}
                      underline="hover"
                    >
                      {recToConfirmBeforeAssignment?.recordName}
                    </Link>
                  </div>

                  <div className={classes.fixedBottomPanelRowAssignmentIcon}>
                    <Tooltip content="Assign" relationship="label">
                      <Pin16Filled
                        onClick={() => {
                          let deletedAssignedRecId = deletedAssignedRecIdRef?.current;
                          if (
                            deletedAssignedRecId &&
                            recToConfirmBeforeAssignment.recordId &&
                            deletedAssignedRecId.toLowerCase().substring(0, 15) ==
                              recToConfirmBeforeAssignment.recordId.toLowerCase().substring(0, 15)
                          ) {
                            setDeletedAssignedRecId(null);
                          }
                          setRecToConfirmBeforeAssignment({
                            recordId: null,
                            recordName: null,
                            objectType: null,
                          });

                          if (
                            sforce &&
                            sforce.opencti &&
                            !activeConnectedCallWhoId &&
                            !activeConnectedCallWhatId &&
                            (recToConfirmBeforeAssignment.recordId.substring(0, 3) == "003" ||
                              recToConfirmBeforeAssignment.recordId.substring(0, 3) == "500" ||
                              recToConfirmBeforeAssignment.recordId.substring(0, 3) == "00T" ||
                              recToConfirmBeforeAssignment.recordId.substring(0, 3) == "00U" ||
                              recToConfirmBeforeAssignment.recordId.substring(0, 3) == "00v")
                          ) {
                            try {
                              sforce.opencti.runApex({
                                apexClass: "unrc.UnaricUnifyClientCtiController",
                                methodName: "getRelatedRecordInfo",
                                methodParams:
                                  "recordId=" +
                                  recToConfirmBeforeAssignment.recordId +
                                  "&recordName=" +
                                  recToConfirmBeforeAssignment?.recordName +
                                  "&objectType=" +
                                  recToConfirmBeforeAssignment?.objectType,
                                callback: (result) => {
                                  if (result?.success && result?.returnValue?.runApex) {
                                    const wrapperJson = result.returnValue.runApex;
                                    const wrapper = wrapperJson ? JSON.parse(wrapperJson) : null;

                                    if (wrapper && (wrapper.whoId || wrapper.whatId)) {
                                      upsertCallMap(activeConnectedCallId, {
                                        whoId: wrapper.whoId,
                                        whoName: wrapper.whoName,
                                        whoType: wrapper.whoType,
                                        whatId: wrapper.whatId,
                                        whatName: wrapper.whatName,
                                        whatType: wrapper.whatType,
                                      });
                                    } else {
                                      handleDefaultAssignment(recToConfirmBeforeAssignment);
                                    }
                                  } else {
                                    console.error(
                                      "page - Error calling Apex getRelatedRecordInfo:",
                                      result?.errors,
                                    );
                                    handleDefaultAssignment(recToConfirmBeforeAssignment);
                                  }
                                },
                              });
                            } catch (ex) {
                              console.warn(
                                "Activecallscreen - call to apex getRelatedRecordInfo Failed :",
                                ex,
                              );
                              handleDefaultAssignment(recToConfirmBeforeAssignment);
                            }
                          } else {
                            handleDefaultAssignment(recToConfirmBeforeAssignment);
                          }
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </>
            )}

            {(activeConnectedCallWhoId || activeConnectedCallWhatId) && (
              <>
                <Divider appearance="strong" className={classes.assignmentDivider} />
                <Caption1 className={classes.relRecHeader}>Related Record</Caption1>
              </>
            )}
            {activeConnectedCallWhoId && (
              <div className={mergeClasses(classes.fixedBottomPanelRowAssignment, classes.mt4)}>
                <div className={classes.fixedBottomPanelRowAssignmentIcon}>
                  <Tooltip content={activeConnectedCallObj.whoType} relationship="label">
                    <SFIcon iconInfo={getIconInfoById(activeConnectedCallWhoId)} />
                  </Tooltip>
                </div>

                <div className={classes.fixedBottomPanelRowAssignmentName}>
                  <Link
                    onClick={() => {
                      screenPop({
                        type: "SOBJECT",
                        params: { recordId: activeConnectedCallWhoId },
                      });
                    }}
                    style={{ color: tokens.colorBrandBackgroundHover }}
                    underline="hover"
                  >
                    {activeConnectedCallObj.whoName}
                  </Link>
                </div>

                <div className={classes.fixedBottomPanelRowAssignmentIcon}>
                  <Tooltip content="Unassign" relationship="label">
                    <Pin16Filled
                      style={{ color: tokens.colorBrandBackgroundHover }}
                      onClick={() => {
                        setDeletedAssignedRecId(activeConnectedCallWhoId);
                        upsertCallMap(activeConnectedCallId, {
                          whoId: null,
                          whoName: null,
                          whoType: null,
                        });
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            )}
            {activeConnectedCallWhatId && (
              <div className={mergeClasses(classes.fixedBottomPanelRowAssignment, classes.mt4)}>
                <div className={classes.fixedBottomPanelRowAssignmentIcon}>
                  <Tooltip content={activeConnectedCallObj.whatType} relationship="label">
                    <SFIcon iconInfo={getIconInfoById(activeConnectedCallWhatId)} />
                  </Tooltip>
                </div>

                <div className={classes.fixedBottomPanelRowAssignmentName}>
                  <Link
                    onClick={() => {
                      screenPop({
                        type: "SOBJECT",
                        params: { recordId: activeConnectedCallWhatId },
                      });
                    }}
                    style={{ color: tokens.colorBrandBackgroundHover }}
                    underline="hover"
                  >
                    {activeConnectedCallObj.whatName}
                  </Link>
                </div>

                <div className={classes.fixedBottomPanelRowAssignmentIcon}>
                  <Tooltip content="Unassign" relationship="label">
                    <Pin16Filled
                      style={{ color: tokens.colorBrandBackgroundHover }}
                      onClick={() => {
                        setDeletedAssignedRecId(activeConnectedCallWhatId);
                        upsertCallMap(activeConnectedCallId, {
                          whatId: null,
                          whatName: null,
                          whatType: null,
                        });
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            )}
            <Divider appearance="strong" className={classes.assignmentDivider} />

            {!isWrapupConnectedCallObjInEditMode && (
              <div className={classes.fixedBottomPanelRow2}>
                <div className={classes.fixedBottomPanelRow2Col}>
                  <Tooltip
                    content={isActiveConnectedCallMuted ? "UnMute" : "Mute"}
                    relationship="label"
                  >
                    <Button
                      aria-pressed={isActiveConnectedCallMuted}
                      onClick={() =>
                        isActiveConnectedCallMuted
                          ? onUnmute(activeConnectedCallObj)
                          : onMute(activeConnectedCallObj)
                      }
                      appearance="outline"
                      disabled={disableBtnInFixedSection()}
                      size="medium"
                      className={classes.fixedBottomPanelRow2ColBtn}
                      disabledClassName={classes.fixedBottomPanelRow2ColBtnDisabled}
                      icon={isActiveConnectedCallMuted ? <MicOff24Filled /> : <Mic24Filled />}
                    />
                  </Tooltip>
                </div>

                <div className={classes.fixedBottomPanelRow2Col}>
                  {isCompleteTransferTooltip ? (
                    <Tooltip content="Complete Transfer" relationship="label">
                      <Button
                        size="medium"
                        appearance="outline"
                        disabled={[
                          "Ringing",
                          "Connecting",
                          "Transferring",
                          "Transferred",
                          "RemoteHold",
                          "Disconnected",
                        ].includes(activeConnectedCallTransferState)}
                        onClick={handleInitTransferClick}
                        className={classes.fixedBottomPanelRow2ColBtn}
                        disabledClassName={classes.fixedBottomPanelRow2ColBtnDisabled}
                        icon={<CallTransferFilled />}
                      />
                    </Tooltip>
                  ) : (
                    <TransferMenu />
                  )}
                </div>

                <div className={classes.fixedBottomPanelRow2Col}>
                  <DtmfDialpad
                    handleDtmf={handleDtmf}
                    activeConnectedCallObj={activeConnectedCallObj}
                    disabled={disableBtnInFixedSection()}
                  />
                </div>

                {/* <div className={classes.fixedBottomPanelRow2Col}>{
                isCallActive && <CallOptionsMenu
                  isVideoOn={localVideoActive}
                  onStartRecording={() => {
                    console.log('onStartRecording')
                  }}
                  onStopRecording={() => {
                    console.log('onStopRecording')
                  }}
                  onStartVideo={onStartVideo}
                  onStopVideo={onStopVideo}
                />
              } 
              <span className={mergeClasses(classes.fixedBottomPanelRow2ColBtnLabel, classes.subText)}>Recording</span>
              </div>*/}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
